export class AudioDirector {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private musicBus: GainNode | null = null;
  private droneNodes: OscillatorNode[] = [];
  private noiseBuffer: AudioBuffer | null = null;
  private lastShotAt = 0;
  private lastHitAt = 0;
  private muted = false;

  get isMuted(): boolean {
    return this.muted;
  }

  async unlock(): Promise<void> {
    if (!this.context) {
      const AudioContextClass = window.AudioContext;
      this.context = new AudioContextClass();
      this.master = this.context.createGain();
      this.master.gain.value = this.muted ? 0 : 0.34;
      this.master.connect(this.context.destination);
    }
    if (this.context.state === "suspended") {
      await this.context.resume();
    }
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (this.context && this.master) {
      this.master.gain.setTargetAtTime(muted ? 0 : 0.34, this.context.currentTime, 0.03);
    }
  }

  toggleMuted(): boolean {
    this.setMuted(!this.muted);
    return this.muted;
  }

  startMusic(stageIndex: number): void {
    if (!this.context || !this.master || this.muted) return;
    this.stopMusic();

    const now = this.context.currentTime;
    this.musicBus = this.context.createGain();
    this.musicBus.gain.setValueAtTime(0, now);
    this.musicBus.gain.linearRampToValueAtTime(0.12, now + 1.8);
    this.musicBus.connect(this.master);

    const roots = [48, 52, 45, 55, 43, 40, 50, 38];
    const root = roots[stageIndex] ?? 48;

    [1, 1.5, 2].forEach((ratio, index) => {
      if (!this.context || !this.musicBus) return;
      const oscillator = this.context.createOscillator();
      const filter = this.context.createBiquadFilter();
      const gain = this.context.createGain();
      oscillator.type = index === 0 ? "sawtooth" : "sine";
      oscillator.frequency.value = root * ratio;
      oscillator.detune.value = (index - 1) * 5;
      filter.type = "lowpass";
      filter.frequency.value = 180 + stageIndex * 22;
      filter.Q.value = 2.5;
      gain.gain.value = index === 0 ? 0.12 : 0.06;
      oscillator.connect(filter);
      filter.connect(gain);
      gain.connect(this.musicBus);
      oscillator.start();
      this.droneNodes.push(oscillator);
    });

    const pulse = this.context.createOscillator();
    const pulseGain = this.context.createGain();
    pulse.type = "square";
    pulse.frequency.value = root * 2;
    pulseGain.gain.value = 0.012;
    pulse.connect(pulseGain);
    pulseGain.connect(this.musicBus);
    pulse.start();
    this.droneNodes.push(pulse);
  }

  intensifyBoss(): void {
    if (!this.context || !this.musicBus) return;
    this.musicBus.gain.setTargetAtTime(0.18, this.context.currentTime, 0.35);
    this.droneNodes.forEach((node, index) => {
      node.detune.setTargetAtTime(index % 2 === 0 ? 18 : -14, this.context!.currentTime, 0.5);
    });
  }

  stopMusic(): void {
    if (!this.context) return;
    const now = this.context.currentTime;
    if (this.musicBus) {
      this.musicBus.gain.cancelScheduledValues(now);
      this.musicBus.gain.setTargetAtTime(0, now, 0.08);
    }
    this.droneNodes.forEach((node) => {
      try {
        node.stop(now + 0.45);
      } catch {
        // An already-stopped oscillator can be ignored.
      }
    });
    this.droneNodes = [];
    this.musicBus = null;
  }

  shoot(level: number): void {
    if (!this.context || performance.now() - this.lastShotAt < 88) return;
    this.lastShotAt = performance.now();
    this.tone({
      frequency: 360 + level * 23,
      endFrequency: 190 + level * 9,
      duration: 0.055,
      volume: 0.045,
      type: "square",
    });
  }

  hit(): void {
    const now = performance.now();
    if (now - this.lastHitAt < 52) return;
    this.lastHitAt = now;
    this.noise(0.035, 0.055, 1200);
  }

  explode(size = 1): void {
    this.noise(0.08 * Math.min(size, 2), 0.17 + size * 0.08, 450);
    this.tone({
      frequency: 110,
      endFrequency: 38,
      duration: 0.24,
      volume: 0.12 * Math.min(size, 1.4),
      type: "sawtooth",
    });
  }

  pickup(kind: "power" | "wing" | "shield" | "repair" | "overdrive" | "bomb"): void {
    const frequency = {
      power: 620,
      wing: 760,
      shield: 520,
      repair: 460,
      overdrive: 880,
      bomb: 390,
    }[kind];
    this.tone({ frequency, endFrequency: frequency * 1.55, duration: 0.18, volume: 0.11, type: "sine" });
  }

  playerHit(): void {
    this.noise(0.16, 0.22, 280);
    this.tone({ frequency: 140, endFrequency: 58, duration: 0.32, volume: 0.17, type: "sawtooth" });
  }

  bomb(): void {
    this.noise(0.28, 0.65, 150);
    this.tone({ frequency: 90, endFrequency: 28, duration: 0.85, volume: 0.22, type: "sine" });
  }

  warning(): void {
    this.tone({ frequency: 250, endFrequency: 180, duration: 0.16, volume: 0.14, type: "square" });
    window.setTimeout(() => {
      this.tone({ frequency: 250, endFrequency: 180, duration: 0.16, volume: 0.14, type: "square" });
    }, 260);
  }

  stageClear(): void {
    [392, 523, 659, 784].forEach((frequency, index) => {
      window.setTimeout(() => {
        this.tone({ frequency, endFrequency: frequency * 1.02, duration: 0.35, volume: 0.1, type: "sine" });
      }, index * 125);
    });
  }

  private tone(options: {
    frequency: number;
    endFrequency: number;
    duration: number;
    volume: number;
    type: OscillatorType;
  }): void {
    if (!this.context || !this.master || this.muted) return;
    const now = this.context.currentTime;
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();
    oscillator.type = options.type;
    oscillator.frequency.setValueAtTime(options.frequency, now);
    oscillator.frequency.exponentialRampToValueAtTime(Math.max(20, options.endFrequency), now + options.duration);
    gain.gain.setValueAtTime(options.volume, now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + options.duration);
    oscillator.connect(gain);
    gain.connect(this.master);
    oscillator.start(now);
    oscillator.stop(now + options.duration + 0.01);
  }

  private noise(volume: number, duration: number, cutoff: number): void {
    if (!this.context || !this.master || this.muted) return;
    if (!this.noiseBuffer) {
      const frameCount = Math.max(1, Math.floor(this.context.sampleRate * 2));
      this.noiseBuffer = this.context.createBuffer(1, frameCount, this.context.sampleRate);
      const data = this.noiseBuffer.getChannelData(0);
      for (let index = 0; index < frameCount; index += 1) {
        data[index] = Math.random() * 2 - 1;
      }
    }
    const source = this.context.createBufferSource();
    const filter = this.context.createBiquadFilter();
    const gain = this.context.createGain();
    const now = this.context.currentTime;
    filter.type = "lowpass";
    filter.frequency.value = cutoff;
    gain.gain.setValueAtTime(volume, now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    source.buffer = this.noiseBuffer;
    source.connect(filter);
    filter.connect(gain);
    gain.connect(this.master);
    const maxOffset = Math.max(0, this.noiseBuffer.duration - duration);
    source.start(now, Math.random() * maxOffset, duration);
  }
}
